import { type SVGProps } from "react";

/**
 * Adaptive color variants for the Djayma logo.
 * Each variant defines fills for: circle, d-bowl, bar, checkmark, wordmark.
 */
type LogoVariant = "light" | "dark" | "on-green" | "on-terracotta" | "on-gold" | "mono-black" | "mono-white";

const VARIANT_COLORS: Record<
  LogoVariant,
  { circle: string; bowl: string; bar: string; check: string; word: string }
> = {
  light: {
    circle: "#2D5A3D",
    bowl: "#FFF8F0",
    bar: "#FFF8F0",
    check: "#D4A535",
    word: "#2D2D2D",
  },
  dark: {
    circle: "#2D5A3D",
    bowl: "#FFF8F0",
    bar: "#FFF8F0",
    check: "#D4A535",
    word: "#FFF8F0",
  },
  "on-green": {
    circle: "#FFF8F0",
    bowl: "#2D5A3D",
    bar: "#2D5A3D",
    check: "#D4A535",
    word: "#FFF8F0",
  },
  "on-terracotta": {
    circle: "#FFF8F0",
    bowl: "#C75B39",
    bar: "#C75B39",
    check: "#D4A535",
    word: "#FFF8F0",
  },
  "on-gold": {
    circle: "#2D5A3D",
    bowl: "#D4A535",
    bar: "#D4A535",
    check: "#FFF8F0",
    word: "#2D5A3D",
  },
  "mono-black": {
    circle: "#2D2D2D",
    bowl: "#FFF",
    bar: "#FFF",
    check: "#FFF",
    word: "#2D2D2D",
  },
  "mono-white": {
    circle: "#FFF",
    bowl: "#1A1A1A",
    bar: "#1A1A1A",
    check: "#1A1A1A",
    word: "#FFFFFF",
  },
};

// --- Icon-only (circular mark) ---

interface DjaymaIconProps extends SVGProps<SVGSVGElement> {
  size?: number;
  variant?: LogoVariant;
}

export function DjaymaIcon({
  size = 32,
  variant = "light",
  ...props
}: DjaymaIconProps) {
  const c = VARIANT_COLORS[variant];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 76 76"
      fill="none"
      aria-hidden="true"
      {...props}
    >
      <circle cx="38" cy="38" r="38" fill={c.circle} />
      <path
        d="M30,16 C18,16 12,24 12,38 C12,52 18,60 30,60 L34,60 L34,16 Z"
        fill={c.bowl}
        opacity="0.95"
      />
      <path
        d="M26,40 L34,50 L56,22"
        fill="none"
        stroke={c.check}
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="34"
        y="16"
        width="10"
        height="44"
        rx="2"
        fill={c.bar}
        opacity="0.95"
      />
    </svg>
  );
}

// --- Full logo (icon + wordmark) ---

interface DjaymaLogoProps extends SVGProps<SVGSVGElement> {
  /** Height of the full logo in px — width scales proportionally */
  height?: number;
  variant?: LogoVariant;
  /** Show only the icon, no wordmark */
  iconOnly?: boolean;
}

export function DjaymaLogo({
  height = 40,
  variant = "light",
  iconOnly = false,
  ...props
}: DjaymaLogoProps) {
  const c = VARIANT_COLORS[variant];

  if (iconOnly) {
    return <DjaymaIcon size={height} variant={variant} {...props} />;
  }

  // Full logo viewBox: icon (60 wide) + gap + wordmark
  const viewBoxWidth = 260;
  const viewBoxHeight = 60;
  const width = (height / viewBoxHeight) * viewBoxWidth;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      fill="none"
      role="img"
      aria-label="Djayma"
      {...props}
    >
      {/* Icon mark */}
      <circle cx="30" cy="30" r="28" fill={c.circle} />
      <path
        d="M24,10 C15,10 10,18 10,30 C10,42 15,50 24,50 L27,50 L27,10 Z"
        fill={c.bowl}
        opacity="0.95"
      />
      <path
        d="M21,32 L27,40 L45,16"
        fill="none"
        stroke={c.check}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="27"
        y="10"
        width="7"
        height="40"
        rx="1.5"
        fill={c.bar}
        opacity="0.95"
      />

      {/* Wordmark */}
      <text
        x="68"
        y="40"
        fontFamily="'Cormorant Garamond', Georgia, serif"
        fontSize="36"
        fill={c.word}
        letterSpacing="-0.3px"
      >
        djayma
      </text>
    </svg>
  );
}

export default DjaymaLogo;
